import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Mail, Phone, Clock, Trash2, ChevronDown, ChevronUp,
  Search, Filter, MessageSquare, CheckCircle, Loader2, StickyNote, Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, de, en) => (lang === 'de' ? de : en);

const STATUS_CONFIG = {
  new: { de: 'Neu', en: 'New', color: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
  read: { de: 'Gelesen', en: 'Read', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20' },
  replied: { de: 'Beantwortet', en: 'Replied', color: 'bg-green-500/15 text-green-400 border-green-500/20' },
  done: { de: 'Erledigt', en: 'Done', color: 'bg-gray-500/15 text-gray-400 border-gray-500/20' },
};

const SUBJECT_LABELS = {
  beratung: { de: 'Kostenlose Beratung', en: 'Free Consultation' },
  haaranalyse: { de: 'KI-Haaranalyse', en: 'AI Hair Analysis' },
  preisanfrage: { de: 'Preisanfrage', en: 'Price Inquiry' },
  termin: { de: 'Termin vereinbaren', en: 'Schedule Appointment' },
  sonstiges: { de: 'Sonstiges', en: 'Other' },
};

function getHeaders() {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const ContactInquiryAdmin = ({ skipAuth = false }) => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const headers = getHeaders();

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/contact-inquiries`, { headers });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data.inquiries || []);
      }
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/contact-inquiries/${id}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status }),
      });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      toast.success(t(language, 'Status aktualisiert', 'Status updated'));
    } catch {
      toast.error('Fehler');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm(t(language, 'Anfrage wirklich löschen?', 'Really delete inquiry?'))) return;
    try {
      await fetch(`${API}/api/admin/contact-inquiries/${id}`, { method: 'DELETE', headers });
      setInquiries(prev => prev.filter(i => i.id !== id));
      if (expandedId === id) setExpandedId(null);
      toast.success(t(language, 'Gelöscht', 'Deleted'));
    } catch {
      toast.error('Fehler');
    }
  };

  const saveNotes = async (id) => {
    try {
      await fetch(`${API}/api/admin/contact-inquiries/${id}/notes`, {
        method: 'PUT', headers, body: JSON.stringify({ notes: notesValue }),
      });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, notes: notesValue } : i));
      setEditingNotes(null);
      toast.success(t(language, 'Notiz gespeichert', 'Note saved'));
    } catch {
      toast.error('Fehler');
    }
  };

  const filtered = inquiries.filter(i => {
    if (filterStatus !== 'all' && i.status !== filterStatus) return false;
    if (filterSubject !== 'all' && i.subject !== filterSubject) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (i.name || '').toLowerCase().includes(s) || (i.email || '').toLowerCase().includes(s) || (i.message || '').toLowerCase().includes(s);
    }
    return true;
  });

  const newCount = inquiries.filter(i => i.status === 'new').length;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getSubjectLabel = (subj) => {
    const labels = SUBJECT_LABELS[subj];
    return labels ? labels[language] || labels.de : subj;
  };

  const cardBg = isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/50' : 'text-gray-500';
  const textM = isDark ? 'text-white/30' : 'text-gray-400';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${textP}`}>
              <MessageSquare className="w-6 h-6 inline-block mr-2 opacity-60" />
              {t(language, 'Kontaktanfragen', 'Contact Inquiries')}
              {newCount > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500 text-white">
                  {newCount} {t(language, 'neu', 'new')}
                </span>
              )}
            </h1>
            <p className={`text-sm mt-1 ${textS}`}>
              {t(language, `${inquiries.length} Anfragen insgesamt`, `${inquiries.length} total inquiries`)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className={`rounded-xl border p-3 mb-4 flex flex-wrap gap-2 items-center ${cardBg}`}>
          <div className="relative flex-1 min-w-[180px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textM}`} />
            <input
              type="text"
              placeholder={t(language, 'Name, E-Mail oder Nachricht...', 'Name, email or message...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={`${inputCls} pl-9`}
              data-testid="contact-search"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className={`${inputCls} w-auto`}
            data-testid="contact-filter-status"
          >
            <option value="all">{t(language, 'Alle Status', 'All Status')}</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v[language] || v.de}</option>
            ))}
          </select>
          <select
            value={filterSubject}
            onChange={e => setFilterSubject(e.target.value)}
            className={`${inputCls} w-auto`}
            data-testid="contact-filter-subject"
          >
            <option value="all">{t(language, 'Alle Betreff', 'All Subjects')}</option>
            {Object.entries(SUBJECT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v[language] || v.de}</option>
            ))}
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className={`w-6 h-6 animate-spin ${textS}`} />
          </div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className={`text-center py-16 ${textS}`}>
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>{t(language, 'Keine Anfragen gefunden', 'No inquiries found')}</p>
          </div>
        )}

        {/* List */}
        {!loading && filtered.map(inq => {
          const expanded = expandedId === inq.id;
          const statusConf = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;

          return (
            <div key={inq.id} className={`rounded-xl border mb-2 overflow-hidden transition-all ${cardBg}`}
                 data-testid={`contact-row-${inq.id}`}>
              {/* Row header */}
              <button
                onClick={() => {
                  setExpandedId(expanded ? null : inq.id);
                  if (!expanded && inq.status === 'new') updateStatus(inq.id, 'read');
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isDark ? 'hover:bg-white/3' : 'hover:bg-gray-50'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm truncate ${textP}`}>{inq.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusConf.color}`}>
                      {statusConf[language] || statusConf.de}
                    </span>
                  </div>
                  <div className={`text-xs mt-0.5 truncate ${textS}`}>
                    {getSubjectLabel(inq.subject)} &middot; {inq.email}
                  </div>
                </div>
                <span className={`text-[11px] flex-shrink-0 ${textM}`}>{formatDate(inq.created_at)}</span>
                {expanded ? <ChevronUp className={`w-4 h-4 flex-shrink-0 ${textM}`} /> : <ChevronDown className={`w-4 h-4 flex-shrink-0 ${textM}`} />}
              </button>

              {/* Expanded detail */}
              {expanded && (
                <div className={`px-4 pb-4 pt-1 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  {/* Contact info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className={`flex items-center gap-2 text-sm ${textS}`}>
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <a href={`mailto:${inq.email}`} className="text-[#7BA7C2] hover:underline truncate">{inq.email}</a>
                    </div>
                    {inq.phone && (
                      <div className={`flex items-center gap-2 text-sm ${textS}`}>
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <a href={`tel:${inq.phone}`} className="text-[#7BA7C2] hover:underline">{inq.phone}</a>
                      </div>
                    )}
                    <div className={`flex items-center gap-2 text-sm ${textS}`}>
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      {formatDate(inq.created_at)}
                    </div>
                  </div>

                  {/* Subject */}
                  <div className={`text-xs font-semibold mb-1 ${textM}`}>{t(language, 'Betreff', 'Subject')}</div>
                  <div className={`text-sm mb-3 ${textP}`}>{getSubjectLabel(inq.subject)}</div>

                  {/* Message */}
                  <div className={`text-xs font-semibold mb-1 ${textM}`}>{t(language, 'Nachricht', 'Message')}</div>
                  <div className={`text-sm whitespace-pre-wrap rounded-lg p-3 mb-4 ${isDark ? 'bg-white/3 text-white/70' : 'bg-gray-50 text-gray-700'}`}>
                    {inq.message || t(language, 'Keine Nachricht', 'No message')}
                  </div>

                  {/* Notes */}
                  <div className={`text-xs font-semibold mb-1 flex items-center gap-1 ${textM}`}>
                    <StickyNote className="w-3 h-3" />
                    {t(language, 'Interne Notizen', 'Internal Notes')}
                  </div>
                  {editingNotes === inq.id ? (
                    <div className="mb-4">
                      <textarea
                        value={notesValue}
                        onChange={e => setNotesValue(e.target.value)}
                        rows={2}
                        className={`${inputCls} mb-2`}
                        placeholder={t(language, 'Notiz hinzufügen...', 'Add note...')}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveNotes(inq.id)} className="bg-[#7BA7C2] text-white text-xs h-7">
                          <Save className="w-3 h-3 mr-1" /> {t(language, 'Speichern', 'Save')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingNotes(null)} className="text-xs h-7">
                          {t(language, 'Abbrechen', 'Cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingNotes(inq.id); setNotesValue(inq.notes || ''); }}
                      className={`text-sm rounded-lg p-2 mb-4 w-full text-left transition-colors ${
                        inq.notes
                          ? (isDark ? 'bg-amber-500/5 text-amber-300/70 border border-amber-500/10' : 'bg-amber-50 text-amber-700 border border-amber-200/50')
                          : (isDark ? 'bg-white/3 text-white/30 hover:bg-white/5' : 'bg-gray-50 text-gray-400 hover:bg-gray-100')
                      }`}
                    >
                      {inq.notes || t(language, 'Klicken um Notiz hinzuzufügen...', 'Click to add note...')}
                    </button>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={inq.status}
                      onChange={e => updateStatus(inq.id, e.target.value)}
                      className={`${inputCls} w-auto text-xs`}
                      data-testid={`contact-status-${inq.id}`}
                    >
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v[language] || v.de}</option>
                      ))}
                    </select>
                    <a
                      href={`mailto:${inq.email}?subject=Re: ${getSubjectLabel(inq.subject)} - Dion Hair Clinic`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#7BA7C2] text-white hover:bg-[#5A8BA6] transition-colors"
                      data-testid={`contact-reply-${inq.id}`}
                    >
                      <Mail className="w-3 h-3" />
                      {t(language, 'Antworten', 'Reply')}
                    </a>
                    {inq.phone && (
                      <a
                        href={`https://wa.me/${inq.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors"
                      >
                        <Phone className="w-3 h-3" /> WhatsApp
                      </a>
                    )}
                    <button
                      onClick={() => deleteInquiry(inq.id)}
                      className={`ml-auto p-1.5 rounded-lg transition-colors ${isDark ? 'text-red-400/50 hover:bg-red-500/10 hover:text-red-400' : 'text-red-300 hover:bg-red-50 hover:text-red-500'}`}
                      data-testid={`contact-delete-${inq.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContactInquiryAdmin;
